import {
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  startOfWeek,
  endOfWeek,
} from "date-fns";

/**
 * Normaliza un rango a día completo en hora LOCAL del navegador.
 * `from` -> 00:00:00.000 ; `to` -> 23:59:59.999
 * Garantiza que un mismo día (from === to) cubra todas las 24h y nunca
 * devuelva un rango vacío. Postgres (timestamptz) interpreta correctamente
 * el ISO con offset que produce toISOString().
 */
export function normalizeDayRange(from: Date, to: Date): { from: Date; to: Date } {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
  // Si por accidente from > to, invertir para no devolver rango vacío.
  if (start.getTime() > end.getTime()) {
    return { from: end, to: start };
  }
  return { from: start, to: end };
}

export const datePresets = [
  { label: "Hoy", value: "today" },
  { label: "Últimos 7 días", value: "last-7-days" },
  { label: "Últimos 30 días", value: "last-30-days" },
  { label: "Esta semana", value: "this-week" },
  { label: "Este mes", value: "this-month" },
  { label: "Mes anterior", value: "last-month" },
  { label: "Trimestre actual", value: "this-quarter" },
  { label: "Trimestre anterior", value: "last-quarter" },
  { label: "Trimestre concreto", value: "quarter-pick" },
  { label: "Año actual", value: "this-year" },
  { label: "Año anterior", value: "last-year" },
  { label: "Personalizar", value: "custom" },
];

export function getDateRange(preset: string, now: Date = new Date()): { from: Date; to: Date } | null {
  switch (preset) {
    case "today":
      return normalizeDayRange(now, now);
    case "last-7-days":
      return normalizeDayRange(subDays(now, 6), now);
    case "last-30-days":
      return normalizeDayRange(subDays(now, 29), now);
    case "this-week":
      return normalizeDayRange(startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 }));
    case "this-month":
      return normalizeDayRange(startOfMonth(now), endOfMonth(now));
    case "last-month": {
      const lm = subMonths(now, 1);
      return normalizeDayRange(startOfMonth(lm), endOfMonth(lm));
    }
    case "this-quarter":
      return normalizeDayRange(startOfQuarter(now), endOfQuarter(now));
    case "last-quarter": {
      const lq = subMonths(now, 3);
      return normalizeDayRange(startOfQuarter(lq), endOfQuarter(lq));
    }
    case "this-year":
      return normalizeDayRange(startOfYear(now), endOfYear(now));
    case "last-year": {
      const ly = new Date(now.getFullYear() - 1, 0, 1);
      return normalizeDayRange(startOfYear(ly), endOfYear(ly));
    }
    default:
      return null;
  }
}

export function formatRangeSummary(from: Date, to: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const d = (x: Date) => `${pad(x.getDate())}/${pad(x.getMonth() + 1)}/${x.getFullYear()}`;
  const t = (x: Date) => `${pad(x.getHours())}:${pad(x.getMinutes())}`;
  const sameDay =
    from.getFullYear() === to.getFullYear() &&
    from.getMonth() === to.getMonth() &&
    from.getDate() === to.getDate();
  if (sameDay) return `${d(from)} ${t(from)}–${t(to)}`;
  return `${d(from)} – ${d(to)}`;
}

/**
 * Metadatos de rango para exportaciones (CSV/JSON). Usa exactamente los
 * mismos timestamps que el filtro activo del Dashboard (mismas ISO en UTC
 * y mismo offset local), garantizando consistencia entre lo mostrado y lo
 * exportado.
 */
export interface ExportRangeMeta {
  presetLabel: string;
  rangeLabel: string;
  fromISO: string | null;
  toISO: string | null;
  timezone: string;
  timezoneOffsetMinutes: number;
}

export function buildExportRangeMeta(
  presetLabel: string,
  range: { from: Date; to: Date } | null
): ExportRangeMeta {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
  if (!range) {
    return {
      presetLabel,
      rangeLabel: "Todo",
      fromISO: null,
      toISO: null,
      timezone: tz,
      timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
    };
  }
  return {
    presetLabel,
    rangeLabel: formatRangeSummary(range.from, range.to),
    fromISO: range.from.toISOString(),
    toISO: range.to.toISOString(),
    timezone: tz,
    timezoneOffsetMinutes: -range.from.getTimezoneOffset(),
  };
}
